import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { 
  UserPlus, FileSearch, Coins, Rocket, 
  CheckCircle, ArrowLeft, Building, Briefcase,
  Lightbulb, Factory, ChartLine, Globe
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const HowItWorks = () => {
  const [investorVisible, setInvestorVisible] = useState(false);
  const [projectVisible, setProjectVisible] = useState(false);
  const investorRef = useRef<HTMLElement>(null);
  const projectRef = useRef<HTMLElement>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === investorRef.current && entry.isIntersecting) {
            setInvestorVisible(true);
          }
          if (entry.target === projectRef.current && entry.isIntersecting) {
            setProjectVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (investorRef.current) observer.observe(investorRef.current);
    if (projectRef.current) observer.observe(projectRef.current);

    return () => observer.disconnect();
  }, []);

  // Translated investor steps
  const investorSteps = [
    {
      icon: UserPlus,
      title: t('howItWorks.investor.step1.title'),
      description: t('howItWorks.investor.step1.description'),
    },
    {
      icon: FileSearch,
      title: t('howItWorks.investor.step2.title'),
      description: t('howItWorks.investor.step2.description'),
    },
    {
      icon: ChartLine,
      title: t('howItWorks.investor.step3.title'),
      description: t('howItWorks.investor.step3.description'),
    },
    {
      icon: CheckCircle,
      title: t('howItWorks.investor.step4.title'),
      description: t('howItWorks.investor.step4.description'),
    },
  ];

  // Translated project steps
  const projectSteps = [
    {
      icon: Building,
      title: t('howItWorks.projectHolder.step1.title'),
      description: t('howItWorks.projectHolder.step1.description'),
    },
    {
      icon: FileSearch,
      title: t('howItWorks.projectHolder.step2.title'),
      description: t('howItWorks.projectHolder.step2.description'),
    },
    {
      icon: Briefcase,
      title: t('howItWorks.projectHolder.step3.title'),
      description: t('howItWorks.projectHolder.step3.description'),
    },
    {
      icon: Rocket,
      title: t('howItWorks.projectHolder.step4.title'),
      description: t('howItWorks.projectHolder.step4.description'),
    },
  ];

  // Translated investment approach items
  const investmentApproachItems = [
    {
      icon: Lightbulb,
      title: t('howItWorks.investmentAreas.research.title'),
      description: t('howItWorks.investmentAreas.research.description'),
    },
    {
      icon: Factory,
      title: t('howItWorks.investmentAreas.inventions.title'),
      description: t('howItWorks.investmentAreas.inventions.description'),
    },
    {
      icon: ChartLine,
      title: t('howItWorks.investmentAreas.manufacturing.title'),
      description: t('howItWorks.investmentAreas.manufacturing.description'),
    },
    {
      icon: Globe,
      title: t('howItWorks.investmentAreas.partnerships.title'),
      description: t('howItWorks.investmentAreas.partnerships.description'),
    },
  ];

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-20 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="text-sm font-semibold text-primary-foreground uppercase tracking-wide animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                {t('howItWorks.hero.title')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {t('howItWorks.hero.subtitle')}
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {t('howItWorks.hero.description')}
              </p>
            </div>
          </div>
        </section>

        {/* For Investors */}
        <section ref={investorRef} className="py-20 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className={`text-center mb-16 transition-all duration-700 ${investorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
                {t('howItWorks.investor.title')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('howItWorks.investor.startInvesting')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                <div dangerouslySetInnerHTML={{ __html: t('howItWorks.investor.subtitle') }} />
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {investorSteps.map((step, index) => (
                <div 
                  key={step.title} 
                  className={`relative transition-all duration-700 ${investorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  {index < investorSteps.length - 1 && (
                    <div className={`hidden lg:block absolute top-12 left-0 w-full h-0.5 bg-border transition-all duration-1000 ${investorVisible ? 'scale-x-100' : 'scale-x-0'}`} style={{ transitionDelay: `${index * 200 + 500}ms` }} />
                  )}
                  <div className="relative bg-card rounded-2xl p-6 shadow-card text-center hover-lift group">
                    <div className="w-16 h-16 rounded-full bg-hero-gradient flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-8 h-8 text-primary-foreground" />
                    </div>
                    <span className="text-sm text-muted-foreground">{t('common.step')} {index + 1}</span>
                    <h3 className="text-lg font-bold mt-1 mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className={`text-center mt-12 transition-all duration-700 delay-500 ${investorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Button asChild size="lg" className="bg-hero-gradient hover:opacity-90 text-primary-foreground gap-2 hover-lift group">
                <Link to="/investor">
                  {t('howItWorks.investor.startInvesting')}
                  <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Investment Approach */}
        <section className="py-20 bg-sidebar text-sidebar-foreground">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('howItWorks.investmentAreas.title')}</h2>
              <p className="text-sidebar-foreground/70 max-w-2xl mx-auto">
                {t('howItWorks.investmentAreas.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {investmentApproachItems.map((item, index) => (
                <div key={index} className="bg-sidebar-accent rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-sidebar-primary flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-sidebar-primary-foreground" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-sidebar-foreground/70 text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* For Project Holders */}
        <section ref={projectRef} className="py-20 bg-sidebar text-sidebar-foreground overflow-hidden">
          <div className="container mx-auto px-4">
            <div className={`text-center mb-16 transition-all duration-700 ${projectVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span className="inline-block px-4 py-2 rounded-full bg-sidebar-primary/20 text-sidebar-primary font-semibold text-sm mb-4">
                {t('howItWorks.projectHolder.title')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold">{t('howItWorks.projectHolder.submitProject')}</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {projectSteps.map((step, index) => (
                <div 
                  key={step.title} 
                  className={`relative transition-all duration-700 ${projectVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  {index < projectSteps.length - 1 && (
                    <div className={`hidden lg:block absolute top-12 left-0 w-full h-0.5 bg-sidebar-border transition-all duration-1000 ${projectVisible ? 'scale-x-100' : 'scale-x-0'}`} style={{ transitionDelay: `${index * 200 + 500}ms` }} />
                  )}
                  <div className="relative bg-sidebar-accent rounded-2xl p-6 text-center hover-lift group">
                    <div className="w-16 h-16 rounded-full bg-sidebar-primary flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-8 h-8 text-sidebar-primary-foreground" />
                    </div>
                    <span className="text-sm text-muted-foreground">{t('common.step')} {index + 1}</span>
                    <h3 className="text-lg font-bold mt-1 mb-2">{step.title}</h3>
                    <p className="text-sm text-sidebar-foreground/70">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className={`text-center mt-12 transition-all duration-700 delay-500 ${projectVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Button asChild size="lg" className="bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 gap-2 hover-lift group">
                <Link to="/program">
                  {t('howItWorks.projectHolder.submitProject')}
                  <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowItWorks;